// One-time welcome dialog shown on first login. Routes the user into the
// one-click demo flow or the spotlight tour. Matte-themed (matches Portfolio).
import React from 'react';
import { Dialog, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory } from 'react-router-dom';
import { C, SANS, MONO, primaryBtnSx } from '../../theme/matteTokens';
import { useOnboarding } from './OnboardingProvider';

const STEPS = [
  { n: '1', title: 'Connect an exchange', body: 'Link Binance or Alpaca with API keys — or spin up a risk-free demo account in one click.' },
  { n: '2', title: 'Add & lock holdings', body: 'Set your starting balances and lock them so we can track real profit & loss.' },
  { n: '3', title: 'Create a bot', body: 'Launch an automated strategy from a ready-made preset, or build your own.' },
];

export default function WelcomeModal({ open, onClose }) {
  const history = useHistory();
  const { markWelcomeSeen, startTour } = useOnboarding();

  const close = () => { markWelcomeSeen && markWelcomeSeen(); onClose && onClose(); };
  const startDemo = () => { close(); history.push('/profile/portfolio?onboard=demo'); };
  const takeTour = () => { markWelcomeSeen && markWelcomeSeen(); onClose && onClose(); startTour && startTour(); };

  return (
    <Dialog open={!!open} onClose={close} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: C.card, color: C.bright, border: `1px solid ${C.hairline}`, borderRadius: '16px', fontFamily: SANS, backgroundImage: 'none' } }}>
      <Box sx={{ p: '28px 28px 24px', position: 'relative' }}>
        <IconButton onClick={close} sx={{ position: 'absolute', top: 12, right: 12, color: C.muted }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontFamily: MONO, fontSize: '11px', letterSpacing: '.14em', color: C.accentText, mb: 1 }}>WELCOME TO CRYPTOLOGICS-AI</Typography>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: C.bright, mb: 0.5 }}>Trade smarter in 3 steps</Typography>
        <Typography sx={{ fontSize: '14px', color: C.body, mb: 3 }}>
          New here? Here's the whole platform in a nutshell — you can try it all with sample data first, no funds required.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3 }}>
          {STEPS.map((s) => (
            <Box key={s.n} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: '14px 16px', bgcolor: C.panel, border: `1px solid ${C.hairline}`, borderRadius: '12px' }}>
              <Box sx={{ flex: '0 0 28px', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontWeight: 700, fontSize: '13px', color: C.accentText, background: 'rgba(77,143,199,.12)', border: '1px solid rgba(77,143,199,.38)' }}>{s.n}</Box>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: C.bright }}>{s.title}</Typography>
                <Typography sx={{ fontSize: '12.5px', color: C.body }}>{s.body}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box component="button" onClick={startDemo} sx={primaryBtnSx('12px 20px')}>Start with a demo →</Box>
          <Box component="button" onClick={takeTour} sx={{ background: 'none', border: 'none', color: C.accentText, fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, cursor: 'pointer' }}>Take a quick tour</Box>
          <Box component="button" onClick={close} sx={{ background: 'none', border: 'none', color: C.muted, fontFamily: SANS, fontSize: '13.5px', cursor: 'pointer', ml: 'auto' }}>Skip</Box>
        </Box>
      </Box>
    </Dialog>
  );
}
